import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { secondaryButtonBase, iconButtonBase } from "../styles/buttonStyles";
import { generateTimeSlots } from "../utils/timeSlots";

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    pointer-events: ${({ isOpen }) => (isOpen ? "all" : "none")};
    transition: opacity 0.3s ease;
`;

const Modal = styled.div`
    background: ${({ theme }) => theme.cardBackground || "#fff"};
    border-radius: 1rem;
    padding: 2rem;
    max-width: 500px;
    width: 100%;
    position: relative;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    transform: ${({ isOpen }) => (isOpen ? "scale(1)" : "scale(0.9)")};
    transition: transform 0.3s ease;
    text-align: left;

    @media (max-width: 768px) {
        padding: 1.5rem;
        max-width: 95%;
    }
`;

const CloseButton = styled.button`
    ${iconButtonBase}
    position: absolute;
    top: 1rem;
    right: 1rem;
    font-size: 1.5rem;
    color: ${({ theme }) => theme.subtleText};

    &:hover {
        color: ${({ theme }) => theme.text};
    }
`;

const Title = styled.h2`
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0 0 1.5rem 0;
    color: ${({ theme }) => theme.text};
    text-align: left !important;

    @media (max-width: 768px) {
        font-size: 1.25rem;
    }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const Label = styled.label`
    font-size: 0.95rem;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
`;

const Input = styled.input`
    padding: 0.75rem;
    font-size: 1rem;
    border: 1px solid ${({ theme }) => theme.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"};
    border-radius: 0.5rem;
    background: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.text};
    transition: border-color 0.2s ease;

    &:focus {
        outline: none;
        border-color: #00bfff;
    }

    &::placeholder {
        color: ${({ theme }) => theme.subtleText};
        opacity: 0.6;
    }
`;

const Select = styled.select`
    padding: 0.75rem;
    font-size: 1rem;
    border: 1px solid ${({ theme }) => theme.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"};
    border-radius: 0.5rem;
    background: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.text};
    transition: border-color 0.2s ease;
    cursor: pointer;
    font-family: inherit;

    &:focus {
        outline: none;
        border-color: #00bfff;
    }

    option {
        background: ${({ theme }) => theme.background};
        color: ${({ theme }) => theme.text};
    }
`;

const OptionsLabel = styled.div`
    font-size: 0.95rem;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    margin-bottom: 0.5rem;
`;

const OptionsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const OptionItem = styled.label`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    padding: 0.75rem;
    border: 1px solid ${({ theme, selected }) =>
        selected ? "#00bfff" : theme.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"
    };
    border-radius: 0.5rem;
    background: ${({ theme, selected }) =>
        selected ? (theme.mode === "dark" ? "rgba(0,191,255,0.1)" : "rgba(0,191,255,0.05)") : "transparent"
    };
    transition: all 0.2s ease;

    &:hover {
        border-color: #00bfff;
    }

    input[type="radio"] {
        margin: 0;
        cursor: pointer;
    }

    span {
        font-size: 0.95rem;
        color: ${({ theme }) => theme.text};
    }
`;

const SubmitButton = styled.button`
    ${secondaryButtonBase}
    padding: 0.875rem 1.5rem;
    border: 2px solid ${({ theme, $isActive }) => $isActive ? theme.primary : 'rgba(0, 191, 255, 0.3)'};
    border-radius: 999px;
    color: ${({ $isActive }) => $isActive ? '#fff' : 'rgba(0, 191, 255, 0.5)'};
    background: ${({ theme, $isActive }) => $isActive ? theme.primary : 'transparent'};
    margin-top: 0.5rem;
    font-weight: 600;
    transition: all 0.2s ease;
    box-shadow: ${({ $isActive }) => $isActive ? '0 2px 8px rgba(0, 191, 255, 0.3)' : 'none'};

    &:hover:not(:disabled) {
        background: ${({ theme, $isActive }) => $isActive ? theme.primary : `${theme.primary}15`};
        border-color: ${({ theme }) => theme.primary};
        color: ${({ $isActive }) => $isActive ? '#fff' : ({ theme }) => theme.primary};
        transform: translateY(-1px);
        filter: ${({ $isActive }) => $isActive ? 'brightness(1.1)' : 'none'};
        box-shadow: ${({ $isActive }) => $isActive ? '0 4px 12px rgba(0, 191, 255, 0.4)' : 'none'};
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        cursor: not-allowed;
    }
`;

const StatusMessage = styled.div.attrs(({ type }) => ({
    role: type === "error" ? "alert" : "status",
    'aria-live': "polite",
    'aria-atomic': "true"
}))`
    padding: 1rem;
    border-radius: 0.5rem;
    font-size: 0.95rem;
    background-color: ${({ type }) => (type === "success" ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)")};
    color: ${({ type }) => (type === "success" ? "#10b981" : "#ef4444")};
    border: 1px solid ${({ type }) => (type === "success" ? "#10b981" : "#ef4444")};
`;

const ContactModal = ({ isOpen, onClose }) => {
    const { days, timeBlocks } = generateTimeSlots();
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        company: "",
        reason: "interview",
        day: "",
        time: ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });

    const modalRef = useRef(null);
    const firstFocusableRef = useRef(null);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === "Escape" && isOpen) {
                onClose();
            }
        };

        const handleTab = (e) => {
            if (!isOpen || e.key !== "Tab") return;

            const focusableElements = modalRef.current?.querySelectorAll(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );

            if (!focusableElements || focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === firstElement) {
                    e.preventDefault();
                    lastElement.focus();
                }
            } else {
                if (document.activeElement === lastElement) {
                    e.preventDefault();
                    firstElement.focus();
                }
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
            document.addEventListener("keydown", handleTab);
            document.body.style.overflow = "hidden";

            // Focus first focusable element when modal opens
            setTimeout(() => {
                const focusableElements = modalRef.current?.querySelectorAll(
                    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                );
                if (focusableElements && focusableElements.length > 0) {
                    focusableElements[0].focus();
                }
            }, 100);
        }

        return () => {
            document.removeEventListener("keydown", handleEscape);
            document.removeEventListener("keydown", handleTab);
            document.body.style.overflow = "unset";
        };
    }, [isOpen, onClose]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus({ type: "", message: "" });

        try {
            const formData = new FormData(e.target);

            const response = await fetch('/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams(formData).toString()
            });

            if (response.ok) {
                setSubmitStatus({
                    type: "success",
                    message: "Thanks! Your message has been sent. I'll get back to you soon."
                });
                setFormData({
                    name: "",
                    email: "",
                    company: "",
                    reason: "interview",
                    day: "",
                    time: ""
                });
                setTimeout(() => {
                    onClose();
                    setSubmitStatus({ type: "", message: "" });
                }, 2000);
            } else {
                setSubmitStatus({
                    type: "error",
                    message: "Something went wrong. Please try again."
                });
            }
        } catch (error) {
            setSubmitStatus({
                type: "error",
                message: "Failed to send message. Please try emailing me directly at nlovett15@gmail.com"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const reasonOptions = [
        { value: "interview", label: "Schedule an Interview" },
        { value: "portfolio", label: "Portfolio Review" }
    ];

    // Check if all required fields are filled
    const isFormValid = formData.name && formData.email && formData.day && formData.time && !isSubmitting;

    return (
        <Overlay isOpen={isOpen} onClick={handleOverlayClick}>
            <Modal
                isOpen={isOpen}
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
            >
                <CloseButton onClick={onClose} aria-label="Close modal" ref={firstFocusableRef}>
                    <FontAwesomeIcon icon={faTimes} />
                </CloseButton>

                <Title id="modal-title">Get in Touch</Title>

                <Form
                    name="contact-modal"
                    method="POST"
                    data-netlify="true"
                    data-netlify-honeypot="bot-field"
                    onSubmit={handleSubmit}
                >
                    <input type="hidden" name="form-name" value="contact-modal" />
                    <input type="hidden" name="bot-field" />

                    {submitStatus.message && (
                        <StatusMessage type={submitStatus.type}>
                            {submitStatus.message}
                        </StatusMessage>
                    )}

                    <Field>
                        <Label htmlFor="name">Name *</Label>
                        <Input
                            id="name"
                            name="name"
                            type="text"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Your full name"
                            required
                        />
                    </Field>

                    <Field>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your.email@company.com"
                            required
                        />
                    </Field>

                    <Field>
                        <Label htmlFor="company">Company</Label>
                        <Input
                            id="company"
                            name="company"
                            type="text"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder="Your company name"
                        />
                    </Field>

                    <div>
                        <OptionsLabel>I'm interested in *</OptionsLabel>
                        <OptionsList>
                            {reasonOptions.map((option) => (
                                <OptionItem
                                    key={option.value}
                                    selected={formData.reason === option.value}
                                >
                                    <input
                                        type="radio"
                                        name="reason"
                                        value={option.value}
                                        checked={formData.reason === option.value}
                                        onChange={handleChange}
                                        required
                                    />
                                    <span>{option.label}</span>
                                </OptionItem>
                            ))}
                        </OptionsList>
                    </div>

                    <Field>
                        <Label htmlFor="day">Preferred Day *</Label>
                        <Select
                            id="day"
                            name="day"
                            value={formData.day}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select a day</option>
                            {days.map((day) => (
                                <option key={day.value} value={day.value}>
                                    {day.label}
                                </option>
                            ))}
                        </Select>
                    </Field>

                    <Field>
                        <Label htmlFor="time">Preferred Time (PST)*</Label>
                        <Select
                            id="time"
                            name="time"
                            value={formData.time}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select a time</option>
                            {timeBlocks.map((block) => (
                                <option key={block.value} value={block.value}>
                                    {block.label}
                                </option>
                            ))}
                        </Select>
                    </Field>

                    <SubmitButton
                        type="submit"
                        disabled={!isFormValid}
                        $isActive={isFormValid}
                        aria-busy={isSubmitting}
                    >
                        {isSubmitting ? "Sending..." : "Send Message"}
                    </SubmitButton>
                </Form>
            </Modal>
        </Overlay>
    );
};

export default ContactModal;
